// src/pages/FundPlanner/components/FundStructurer/FundStats.jsx
import React, { useMemo } from "react";
import dayjs from "lib/dayjs";
import SectionCard from "components/SectionCard";
import {
  computePortfolioDV01,
  computeWAL,
  computeWAM,
} from "lib/bondMath";
import { computeSlgsDollarDayWeightedRate } from "pages/FundPlanner/lib/structurerMetrics";
import { useFundPlanner } from "pages/FundPlanner/context/StructurerContext";

export default function FundStats({
  allocations = [],
  sufficiency = [],
}) {
  const { settlementDate } = useFundPlanner();

  /** ---------- Formatters ---------- */
  const fmt2 = (v) =>
    v != null
      ? Number(v).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "";

  const fmtPct = (v) =>
    v != null
      ? `${Number(v).toLocaleString(undefined, {
          minimumFractionDigits: 6,
          maximumFractionDigits: 6,
        })}%`
      : "";

  const fmt3 = (v) =>
    v != null
      ? Number(v).toLocaleString(undefined, {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        })
      : "";

  /** ---------- Computations ---------- */
  const stats = useMemo(() => {
    if (!allocations?.length) return null;

    const totalInvested = allocations.reduce(
      (sum, a) => sum + (a.cost || 0),
      0
    );

    const totalPrincipal = allocations.reduce(
      (sum, a) => sum + (a.principal || 0),
      0
    );

    const totalInterest = allocations.reduce(
      (sum, a) => sum + (a.totalInterest || 0),
      0
    );

    /** ------------------------------------------------------------
     * Total Cash Outflows (from sufficiency)
     * ------------------------------------------------------------ */
    const totalCashOutflows = Array.isArray(
      sufficiency
    )
      ? sufficiency.reduce(
          (sum, row) =>
            sum + (Number(row.outflow) || 0),
          0
        )
      : 0;

    const numSecurities = allocations.length;

    /** ------------------------------------------------------------
     * Weighted Average Coupon
     * ------------------------------------------------------------ */
    const weightedAvgCoupon =
      totalPrincipal > 0
        ? allocations.reduce(
            (sum, a) =>
              sum +
              (a.ratePct * a.principal) /
                totalPrincipal,
            0
          )
        : 0;

    const effectivePortfolioYield = computeSlgsDollarDayWeightedRate(
      allocations,
      settlementDate
    );

    /** ------------------------------------------------------------
     * Weighted Average Maturity (Market-Value Weighted)
     * ------------------------------------------------------------ */
    const weightedAvgMaturityYears = computeWAM(
      allocations,
      settlementDate,
      "act/365"
    );

    /** ------------------------------------------------------------
     * Weighted Average Life (Principal-Weighted)
     * ------------------------------------------------------------ */
    const weightedAvgLifeYears = computeWAL(
      allocations.map((a) => ({
        date: a.maturityDate,
        principal:
          Number(a.principal) || 0,
      })),
      settlementDate,
      "act/365"
    );

    const maturityDates = allocations.map((a) =>
      dayjs(a.maturityDate)
    );

    const earliestMaturity =
      maturityDates.length
        ? dayjs
            .min(maturityDates)
            .format("MMM D, YYYY")
        : "-";

    const latestMaturity =
      maturityDates.length
        ? dayjs
            .max(maturityDates)
            .format("MMM D, YYYY")
        : "-";

    const dv01Total = computePortfolioDV01(
      allocations,
      2,
      settlementDate
    );

    return {
      totalInvested,
      totalPrincipal,
      totalCashOutflows,
      totalInterest,
      numSecurities,
      weightedAvgCoupon,
      portfolioXirrPct: effectivePortfolioYield,
      weightedAvgMaturityYears,
      weightedAvgLifeYears,
      earliestMaturity,
      latestMaturity,
      dv01Total,
    };
  }, [
    allocations,
    sufficiency,
    settlementDate,
  ]);

  if (!stats) {
    return (
      <div className="text-muted italic">
        No investment data available yet.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <SectionCard title="Summary">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-2 text-app">
          <Metric
            label="Total Cash Outflows"
            value={`$${fmt2(
              stats.totalCashOutflows
            )}`}
          />

          <Metric
            label="Weighted Avg Coupon"
            value={fmtPct(
              stats.weightedAvgCoupon
            )}
          />

          <Metric
            label="Number of Positions"
            value={stats.numSecurities}
          />

          <Metric
            label="Earliest Maturity"
            value={stats.earliestMaturity}
          />

          <Metric
            label="Latest Maturity"
            value={stats.latestMaturity}
          />

          <Metric
            label="Weighted Avg Life"
            value={`${fmt3(
              stats.weightedAvgLifeYears
            )} years`}
          />

          <Metric
            label="Portfolio DV01"
            value={`$${fmt2(
              stats.dv01Total
            )}`}
          />
        </div>
      </SectionCard>

      <SectionCard title="Performance">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-2 text-app">
          <Metric
            label="Total Investment Cost"
            value={`$${fmt2(
              stats.totalInvested
            )}`}
          />

          <Metric
            label="Total Interest Earned"
            value={`$${fmt2(
              stats.totalInterest
            )}`}
          />

          <Metric
            label="Effective Portfolio Yield"
            value={fmtPct(
              stats.portfolioXirrPct
            )}
          />
        </div>
      </SectionCard>
    </div>
  );
}

/** ---------- Small subcomponent for uniform display ---------- */
function Metric({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-muted text-sm">
        {label}
      </span>

      <span className="font-medium">
        {value}
      </span>
    </div>
  );
}
